import React, { useState, useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import SessionManager from './SessionManager';

// Use HashRouter for Electron (file:// protocol), BrowserRouter for web
const isElectron = window.location.protocol === 'file:' || window.electron !== undefined;
const Router = isElectron ? HashRouter : BrowserRouter;
import { I18nextProvider } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import CustomDropdown from './CustomDropdown';
import 'flag-icons/css/flag-icons.min.css';
import './Auth.css';
import './GlobalScrollbar.css';
import { preloadTracks } from './hooks/musicCache';
import {
  BOOT_PRELOAD_SOUNDS,
  BOOT_PRELOAD_VIDEO,
  preloadSoundEffectPaths,
  preloadVideo,
} from './hooks/mediaPreload';
import { useContainerScale } from './hooks/useViewportScale';
import { API_URL } from './config/apiConfig';
import {
  isCapacitorNative,
  buildCapacitorGoogleAuthUrl,
  buildWebOrFileGoogleAuthUrl,
} from './mobile/oauthDeepLink';
import { CapacitorNativeBridge } from './mobile/capacitorNativeBridge';


import Strategy from './Strategy';
import CommandCenter from './CommandCenter';
import Welcome from './Welcome';
import BattleOverview from './BattleOverview';
import BattleUI from './BattleUI';
import DemoOrder from './DemoOrder';
import AfterBattle from './AfterBattle';
import HonorHall from './HonorHall';
import Lore from './Lore';
import BlackOps from './BlackOps';
import WareEffort from './WareEffort';
import Profile from './Profile';
import Bank from './Bank';
import Campaign from './Campaign';
import CampaignBattleUI from './campaign/CampaignBattleUI';
import ProvingGrounds from './ProvingGrounds';
import Autobattler from './Autobattler';
import DevAutobattler from './DevAutobattler';
import DispatchAsset from './DispatchAsset';
import ExploreUnknown from './ExploreUnknown';
import Tutorial from './Tutorial';
import Register from './Register';
import ConfirmEmail from './ConfirmEmail';
import ResendConfirmation from './ResendConfirmation';
import RequestPasswordReset from './RequestPasswordReset';
import ResetPassword from './ResetPassword';
import GoogleCallback from './GoogleCallback';
import { FriendsProvider, FriendsModal, ChatModal } from './friends';
import { MusicProvider } from './music';
import OrientationLock, { isWebMobileAllowedUsername } from './OrientationLock';
import PostLoginVideoPage from './PostLoginVideoPage';
import { POST_LOGIN_VIDEO_PATH, POST_LOGIN_VIDEO_SRC } from './PostLoginVideo';
import CustomCursor from './components/CustomCursor';
import { UpdateNotification } from './updates';
import { useDiscordPresenceAuto } from './hooks/useDiscordPresence';
import WarEffortWarnings from './WarEffortWarnings';


// --- Resolution simulator badge (when URL has ?simulate=WIDTHxHEIGHT) ---
function ResolutionSimulatorBadge() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    simulatedResolution,
    baseWidth,
    baseHeight,
    isMobile,
    isLargeDesktop,
    scale,
  } = useContainerScale();
  const params = new URLSearchParams(location.search);
  const sim = params.get('simulate');
  if (!sim || !simulatedResolution) return null;
  const clearSimulate = (e) => {
    e.preventDefault();
    navigate(location.pathname, { replace: true });
  };
  const layoutMode = isMobile ? 'mobile' : isLargeDesktop ? 'large-desktop (WQHD+)' : 'desktop';
  const realW = typeof window !== 'undefined' ? window.innerWidth : 0;
  const realH = typeof window !== 'undefined' ? window.innerHeight : 0;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '10px 16px',
        background: 'rgba(0,0,0,0.9)',
        border: '2px solid rgba(255,200,100,0.8)',
        borderRadius: 8,
        color: '#ffc864',
        fontSize: 13,
        fontWeight: 'bold',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span>Simulating {sim}</span>
        <button
          type="button"
          onClick={clearSimulate}
          style={{
            padding: '4px 10px',
            background: 'rgba(255,200,100,0.25)',
            border: '1px solid rgba(255,200,100,0.6)',
            borderRadius: 4,
            color: '#ffc864',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          Clear
        </button>
      </div>
      <div style={{ fontSize: 11, fontWeight: 'normal', color: 'rgba(255,200,100,0.9)', textAlign: 'center' }}>
        Your screen: {realW}×{realH} → layout: {baseWidth}×{baseHeight} ({layoutMode}) at {(scale * 100).toFixed(0)}%
      </div>
    </div>
  );
}

// --- LoginPage Component ---
function LoginPage({ setUser }) {
  // Load saved username and password if "Remember Me" was previously checked
  const [username, setUsername] = useState(() => {
    const saved = localStorage.getItem('rememberedUsername');
    return saved || '';
  });
  const [password, setPassword] = useState(() => {
    const saved = localStorage.getItem('rememberedPassword');
    return saved || '';
  });
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    // Check if we have a saved username (means remember me was checked)
    return !!localStorage.getItem('rememberedUsername');
  });
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('i18nextLng') || 'en-us';
    return saved;
  });

  useEffect(() => {
    preloadVideo(POST_LOGIN_VIDEO_SRC).catch((err) => {
      console.warn('Post-login video preload failed (non-critical):', err?.message || err);
    });
  }, []);

  // Initialize language from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('i18nextLng') || 'en-us';
    // Convert to normalized format for i18next
    const normalized = saved === 'en-us' ? 'en-US' : 
                      saved === 'de-de' ? 'de-DE' : 
                      saved === 'es-es' ? 'es-ES' : saved;
    i18nInstance.changeLanguage(normalized);
    setCurrentLanguage(saved);
  }, [i18nInstance]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      // Convert normalized language back to lowercase format for select
      const lowercase = lng === 'en-US' ? 'en-us' :
                       lng === 'de-DE' ? 'de-de' :
                       lng === 'es-ES' ? 'es-es' : lng;
      setCurrentLanguage(lowercase);
    };
    
    i18nInstance.on('languageChanged', handleLanguageChanged);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, [i18nInstance]);

  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    console.log('Language change requested:', { newLanguage, current: i18nInstance.language });
    
    // Update state immediately for UI responsiveness
    setCurrentLanguage(newLanguage);
    localStorage.setItem('i18nextLng', newLanguage);
    
    // Convert to normalized format for i18next (en-US, de-DE, es-ES)
    const normalized = newLanguage === 'en-us' ? 'en-US' : 
                      newLanguage === 'de-de' ? 'de-DE' : 
                      newLanguage === 'es-es' ? 'es-ES' : newLanguage;
    
    console.log('Changing language to normalized format:', normalized);
    
    // Change language - this will trigger a re-render with new translations
    i18nInstance.changeLanguage(normalized).then(() => {
      console.log('Language changed successfully:', {
        newLanguage: i18nInstance.language,
        testTranslation: i18nInstance.t('login.title')
      });
      // Ensure state is in sync
      setCurrentLanguage(newLanguage);
    }).catch((err) => {
      console.error('Language change failed:', err);
    });
  };

  const handleLogin = async () => {
    try {
      const apiUrl = API_URL;
      const response = await fetch(`${apiUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error('Login response was not JSON:', parseErr);
        setError(t('login.errorFailed'));
        return;
      }

      if (!response.ok) {
        if (response.status === 403 && data.message) {
          setError(data.message);
        } else {
          setError(data.error || t('login.errorFailed'));
        }
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', data.user?.username || username);

      const decoded = jwtDecode(data.token);
      localStorage.setItem('userId', decoded.userId);

      // Save username and password if "Remember Me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', username);
        localStorage.setItem('rememberedPassword', password);
      } else {
        // Clear saved credentials if "Remember Me" is unchecked
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
      }

      setUser({ username: data.user?.username || username, userId: decoded.userId });
      navigate(POST_LOGIN_VIDEO_PATH);
    } catch (err) {
      console.error('Login request failed:', err);
      const isNetwork =
        typeof err?.message === 'string' && err.message.includes('Failed to fetch');
      if (isNetwork) {
        setError(`${t('login.errorConnectionFailure')} ${t('login.errorNetworkHint')}`);
      } else {
        setError(
          `${t('login.errorFailed')}${err?.message ? `: ${err.message}` : ''}`
        );
      }
    }
  };

  const handleGoogleSignIn = () => {
    console.log('🔵 handleGoogleSignIn called');
    console.log('   Protocol:', window.location.protocol);
    console.log('   window.electron:', window.electron !== undefined ? 'exists' : 'undefined');
    console.log('   API_URL:', API_URL);
    
    // Check if we're in Electron
    const isInElectron = window.electron !== undefined && window.electron.openOAuthWindow;
    
    if (isInElectron) {
      // Electron: Open OAuth in a separate popup window
      // This keeps the main window on file:// protocol so images load correctly
      console.log('🔵 Opening OAuth in popup window (Electron mode)');
      // Pass electron_redirect parameter so server can redirect back to file:// protocol
      const electronRedirect = encodeURIComponent(window.location.href);
      const authUrl = `${API_URL}/auth/google?electron_redirect=${electronRedirect}`;
      window.electron.openOAuthWindow(authUrl);
      
      // Listen for OAuth success
      window.electron.onOAuthSuccess((data) => {
        console.log('✅ OAuth success received:', data.username);
        
        // Store tokens in localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('userId', data.userId);
        localStorage.setItem('user', data.username);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        // Update user state
        setUser({ username: data.username, userId: parseInt(data.userId) });
        
        // Clean up listener
        window.electron.removeOAuthListener();
        
        console.log('✅ User logged in via Google OAuth');
        
        navigate(POST_LOGIN_VIDEO_PATH);
      });
    } else if (isCapacitorNative()) {
      const targetUrl = buildCapacitorGoogleAuthUrl();
      console.log('🔵 Navigating to (Capacitor native):', targetUrl);
      window.location.href = targetUrl;
    } else {
      const targetUrl = buildWebOrFileGoogleAuthUrl();
      console.log('🔵 Navigating to (Web mode):', targetUrl);
      window.location.href = targetUrl;
    }
  };

  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  return (
    <>
        <div className="auth-viewport-wrapper">
          <div className="auth-fullscreen-background" />
          <div 
            className="auth-viewport-container"
            style={{
              width: `${containerBaseWidth}px`,
              height: `${containerBaseHeight}px`,
              transformOrigin: '0 0',
              transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
            }}
          >
            <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
              <div className="auth-card">
                <img 
                  src="/images/logo.png" 
                  alt="Chronicles of the Dying: Anthology" 
                  className="auth-logo"
                />
                
                <form className="auth-form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                  <div className="auth-input-group">
                    <input
                      type="text"
                      className="auth-input"
                      placeholder={t('login.usernameOrEmail')}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>

                  <div className="auth-input-group">
                    <input
                      type="password"
                      className="auth-input"
                      placeholder={t('login.password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div className="auth-remember-me">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRememberMe(checked);
                        // If unchecking, clear saved credentials
                        if (!checked) {
                          localStorage.removeItem('rememberedUsername');
                          localStorage.removeItem('rememberedPassword');
                        }
                      }}
                    />
                    <label htmlFor="rememberMe">
                      {t('login.rememberLogin')}
                    </label>
                  </div>

                  {error && (
                    <div className="auth-error">
                      {error}
                      {error.includes('not confirmed') && (
                        <div style={{ marginTop: 8 }}>
                          <Link to="/resend-confirmation" className="auth-link">
                            {t('login.resendConfirmation')}
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  <button 
                    type="submit"
                    className="auth-button"
                  >
                    {t('login.button')}
                  </button>
                </form>

                <div className="auth-divider">
                  <span>{t('login.or')}</span>
                </div>

                <button
                  onClick={handleGoogleSignIn}
                  className="auth-button-google"
                >
                  <svg className="google-logo" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <g fill="#000" fillRule="evenodd">
                      <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
                      <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.08-1.79 2.68l2.78 2.16c2.01-1.86 3.17-4.6 3.17-7.34z" fill="#4285F4"/>
                      <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
                      <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.78-2.16c-.76.53-1.78.9-3.18.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
                    </g>
                  </svg>
                  {t('login.googleSignIn')}
                </button>

                <div className="auth-links">
                  <Link to="/register" className="auth-link">{t('login.noAccount')}</Link>
                  <Link to="/request-password-reset" className="auth-link">{t('login.forgotPassword')}</Link>
                </div>

                {/* Language Selector */}
                <div className="auth-language-selector-inline">
                  <CustomDropdown
                    options={[
                      { value: 'en-us', label: t('common.languageEnglish'), flags: ['gb', 'us', 'au'] },
                      { value: 'de-de', label: t('common.languageGerman'), flags: ['de', 'at', 'ch'] },
                      { value: 'es-es', label: t('common.languageSpanish'), flags: ['es', 'co', 'ar'] }
                    ]}
                    value={currentLanguage}
                    onChange={(value) => {
                      const event = { target: { value } };
                      handleLanguageChange(event);
                    }}
                    placeholder={t('common.languageEnglish')}
                    openUpward={isContainerMobile}
                    renderOption={(option) => (
                      <span style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px'
                      }}>
                        <span style={{ display: 'flex', gap: '4px' }}>
                          {option.flags && option.flags.map((flag, idx) => (
                            <span key={idx} className={`fi fi-${flag}`} style={{ fontSize: isContainerMobile ? '0.9em' : '1.1em' }}></span>
                          ))}
                        </span>
                        <span>{option.label}</span>
                      </span>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
    </>
  );
}

// --- Navigation Handler Component (handles ESC key for back navigation) ---
function NavigationHandler({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle ESC key
      if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
        // Don't navigate if:
        // 1. User is typing in an input/textarea (let them cancel input first)
        // 2. A modal is open (modals should handle ESC themselves)
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );

        // Check if a modal is open (common modal classes/attributes)
        // [data-block-global-esc-back]: battle-end victory/defeat overlay (BattleUI) and /after-battle — avoid navigate(-1) during rewards
        const hasOpenModal = document.querySelector('.modal-open, [role="dialog"], [role="alertdialog"], [data-resign-dialog-overlay], .mulligan-dialog, .counterstrike-dialog, .friends-modal, .chat-modal, [data-block-global-esc-back]');

        if (isInputFocused || hasOpenModal) {
          // Let the input or modal handle ESC first
          return;
        }

        // Prevent default ESC behavior
        event.preventDefault();
        event.stopPropagation();

        // Navigate back if there's history
        if (location.pathname === POST_LOGIN_VIDEO_PATH) {
          return;
        }

        // Don't navigate back from login page or register page
        if (location.pathname !== '/' && location.pathname !== '/register') {
          navigate(-1);
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, location.pathname]);

  // FFA War Effort: when mission has a winner, other players in battle get war-effort-mission-ended → show mission-over
  useEffect(() => {
    const handler = () => {
      navigate('/wareffort', { replace: true });
    };
    window.addEventListener('war-effort-mission-ended', handler);
    return () => window.removeEventListener('war-effort-mission-ended', handler);
  }, [navigate]);

  return <>{children}</>;
}

// --- Discord Presence Wrapper (must be inside Router) ---
function DiscordPresenceWrapper() {
  const location = useLocation();
  
  // ✅ FIX: Always call hooks unconditionally - useDiscordPresenceAuto handles battle routes internally
  useDiscordPresenceAuto();
  
  // Listen for Discord presence logs from main process
  useEffect(() => {
    if (window.electron && window.electron.discord && window.electron.discord.onPresenceLog) {
      window.electron.discord.onPresenceLog((message) => {
        console.log('📡 Discord Rich Presence:', message);
      });
    }
  }, []);
  
  return null;
}

// --- Main App Component ---
/**
 * Mount a fresh BattleUI per session id. Without this, navigating /battle/A → /battle/B reuses the same
 * component instance and keeps the previous gameState (e.g. winner), which instantly triggers after-battle.
 */
function BattleRoute({ user, token, onLogout }) {
  const { gameId } = useParams();
  return (
    <BattleUI
      key={gameId}
      user={user}
      userId={user?.userId}
      token={token}
      onLogout={onLogout}
    />
  );
}

function CampaignBattleRoute({ user, token, onLogout }) {
  const { missionId } = useParams();
  return (
    <CampaignBattleUI
      key={missionId}
      user={user}
      userId={user?.userId}
      token={token}
      onLogout={onLogout}
    />
  );
}

function App() {
  const [user, setUser] = useState(() => {
    const username = localStorage.getItem('user');
    const userId = localStorage.getItem('userId');
    return username && userId ? { username, userId: parseInt(userId) } : null;
  });

  const handleSetUser = (userObj) => {
    if (userObj) {
      setUser(userObj);
      localStorage.setItem('user', userObj.username);
      localStorage.setItem('userId', userObj.userId);
    } else {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken'); // ✅ Clear refresh token on logout
    }
  };

  // Extract tokens from URL hash if present (for Electron OAuth redirect)
  // This runs after setUser is defined
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      try {
        // Extract query params from hash (format: #/welcome?token=...&userId=...)
        const hashMatch = hash.match(/\?(.+)$/);
        if (hashMatch) {
          const params = new URLSearchParams(hashMatch[1]);
          const token = params.get('token');
          const userId = params.get('userId');
          const username = params.get('username');
          const refreshToken = params.get('refreshToken');
          
          if (token && userId && username) {
            console.log('📱 Extracting OAuth tokens from URL hash');
            // Store tokens in localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('userId', userId);
            localStorage.setItem('user', decodeURIComponent(username));
            if (refreshToken) {
              localStorage.setItem('refreshToken', refreshToken);
            }
            
            // Update user state
            setUser({ username: decodeURIComponent(username), userId: parseInt(userId) });
            
            window.location.hash = '#/post-login-video';
          }
        }
      } catch (e) {
        console.error('Error extracting tokens from URL:', e);
      }
    }
  }, [setUser]);

  const token = localStorage.getItem('token') || '';

  // Preload welcome video, UI sounds, and common music as soon as the shell mounts (no artificial delay)
  useEffect(() => {
    const commonTracks = [
      '/music/main_theme.mp3', // hub menus (welcome, strategy, battle overview, etc.)
    ];

    preloadVideo(BOOT_PRELOAD_VIDEO).catch((err) => {
      console.warn('Background video preload failed (non-critical):', err?.message || err);
    });
    preloadSoundEffectPaths(BOOT_PRELOAD_SOUNDS).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        console.warn('UI sound preload: some failed (non-critical)', failed);
      }
    });
    preloadTracks(commonTracks).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        console.warn('Music preload: some tracks failed (non-critical)', failed);
      }
    });
  }, []);

  // Mobile web: block full app unless logged out (login/register) or explicit allow-list (e.g. player1 for QA).
  const allowWebMobile =
    !user || isWebMobileAllowedUsername(user.username);

  return (
    <OrientationLock allowWebMobile={allowWebMobile}>
      <I18nextProvider i18n={i18n}>
        <MusicProvider>
          <FriendsProvider>
            <Router>
            <CapacitorNativeBridge setUser={handleSetUser} />
            <NavigationHandler>
            {/* Discord Rich Presence - must be inside Router */}
            <DiscordPresenceWrapper />
            <SessionManager onLogout={() => handleSetUser(null)} />
            {/* Global friends modals - available on all pages */}
            <FriendsModal />
            <ChatModal />
            {/* Global custom cursor - visible in Discord/OBS streams */}
            <CustomCursor />
            {/* Global update notification - only shows in Electron */}
            <UpdateNotification />
            {/* War Effort warnings (fortified/HQ under attack, opponent 40/45/50 cards) + alarm - shown everywhere */}
            <WarEffortWarnings />
            {/* Resolution simulator: show badge when ?simulate=WxH is in URL (e.g. ?simulate=2560x1440) */}
            <ResolutionSimulatorBadge />
            <Routes>
            <Route path="/" element={<LoginPage setUser={handleSetUser} />} />
            <Route path="/register" element={<Register setUser={handleSetUser} />} />
            <Route path="/confirm-email/:token" element={<ConfirmEmail />} />
            <Route path="/resend-confirmation" element={<ResendConfirmation />} />
            <Route path="/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/auth/google/callback" element={<GoogleCallback setUser={handleSetUser} />} />
            <Route path="/post-login-video" element={<PostLoginVideoPage />} />
            <Route path="/welcome" element={<Welcome user={user} onLogout={() => handleSetUser(null)} />} />
            <Route path="/strategy" element={<Strategy user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/command-center" element={<CommandCenter user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/honor-hall" element={<HonorHall user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/battleoverview" element={<BattleOverview user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/explore-unknown" element={<ExploreUnknown user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/blackops" element={<BlackOps user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/wareffort" element={<WareEffort user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/campaign" element={<Campaign user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/provinggrounds" element={<ProvingGrounds user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/autobattler" element={<Autobattler user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/dev-autobattler" element={<DevAutobattler user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/dispatch-asset" element={<DispatchAsset user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/tutorial" element={<Tutorial user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/profile" element={<Profile user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/bank" element={<Bank user={user?.username} onLogout={() => handleSetUser(null)} />} />
            <Route path="/battle/:gameId" element={<BattleRoute user={user} token={token} onLogout={() => handleSetUser(null)} />} />
            <Route path="/campaign-battle/:missionId" element={<CampaignBattleRoute user={user} token={token} onLogout={() => handleSetUser(null)} />} />
            <Route path="/after-battle" element={<AfterBattle />} />
            <Route path="/demo-order" element={<DemoOrder />} />
            <Route path="/lore" element={<Lore user={user?.username} />} />
          </Routes>
          </NavigationHandler>
          </Router>
        </FriendsProvider>
        </MusicProvider>
      </I18nextProvider>
    </OrientationLock>
  );
}

export default App;
